import { useState } from 'react'
import { Settings, Shield, Activity, X, Plus, Trash2, Edit2, Server, Clock, AlertTriangle } from 'lucide-react'
import { useApp } from '../context/AppContext'
import { getStatusColor, formatDateShort, generateId } from '../utils/helpers'

const emptyModel = {
  provider: 'OpenAI', baseUrl: '', modelName: '', apiKeyConfigured: false,
  dailyBudget: 0, monthlyBudget: 0, maxTokens: 4096, temperature: 0.7, enabled: false,
  backupModel: '', callLogs: 0, failureCount: 0, lastCalledAt: ''
}

const apiEndpoints = [
  { path: '/api/run-agent', desc: '执行单 Agent', method: 'POST' },
  { path: '/api/run-workflow-step', desc: '执行工作流 Step', method: 'POST' },
  { path: '/api/upload-file', desc: '上传文件', method: 'POST' },
  { path: '/api/parse-file', desc: '解析文件', method: 'POST' },
  { path: '/api/scrape-url', desc: '抓取网页', method: 'POST' },
  { path: '/api/generate-visual-batch', desc: '批量生成视觉任务', method: 'POST' },
  { path: '/api/generate-qa-batch', desc: '批量生成 QA', method: 'POST' },
  { path: '/api/save-review', desc: '保存复盘', method: 'POST' },
  { path: '/api/model-status', desc: '模型状态', method: 'GET' },
]

export default function ModelGateway() {
  const { modelConfigs, setModelConfigs, canEdit } = useApp()
  const [showModal, setShowModal] = useState(false)
  const [editingModel, setEditingModel] = useState(null)
  const [form, setForm] = useState(emptyModel)

  const openModal = (model = null) => {
    if (model) { setEditingModel(model); setForm({ ...model }) }
    else { setEditingModel(null); setForm({ ...emptyModel, id: generateId() }) }
    setShowModal(true)
  }

  const save = () => {
    if (!form.provider || !form.baseUrl || !form.modelName) return alert('请填写完整')
    if (editingModel) { setModelConfigs(modelConfigs.map(m => m.id === editingModel.id ? { ...form } : m)) }
    else { setModelConfigs([...modelConfigs, { ...form, id: generateId() }]) }
    setShowModal(false)
  }

  const remove = (id) => { if (!confirm('确定删除？')) return; setModelConfigs(modelConfigs.filter(m => m.id !== id)) }

  const toggleEnable = (id) => {
    if (!canEdit()) return alert('无权限')
    setModelConfigs(modelConfigs.map(m => m.id === id ? { ...m, enabled: !m.enabled } : m))
  }

  const totalCalls = modelConfigs.reduce((s, m) => s + m.callLogs, 0)
  const totalFailures = modelConfigs.reduce((s, m) => s + m.failureCount, 0)

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-800">模型 API 网关配置</h2>
          <p className="text-sm text-slate-500 mt-1">API Key 不保存在前端，仅显示配置状态。已预留 9 个后端接口。</p>
        </div>
        {canEdit() && (
          <button onClick={() => openModal()} className="btn-primary flex items-center gap-2">
            <Plus size={16} /> 新增配置
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="card p-4 flex items-center gap-3">
          <Server size={20} className="text-primary-600" />
          <div>
            <div className="text-xs text-slate-500">总调用次数</div>
            <div className="text-lg font-bold">{totalCalls.toLocaleString()}</div>
          </div>
        </div>
        <div className="card p-4 flex items-center gap-3">
          <AlertTriangle size={20} className="text-red-500" />
          <div>
            <div className="text-xs text-slate-500">总失败次数</div>
            <div className="text-lg font-bold">{totalFailures.toLocaleString()}</div>
          </div>
        </div>
        <div className="card p-4 flex items-center gap-3">
          <Activity size={20} className="text-emerald-500" />
          <div>
            <div className="text-xs text-slate-500">启用模型</div>
            <div className="text-lg font-bold">{modelConfigs.filter(m => m.enabled).length} / {modelConfigs.length}</div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {modelConfigs.map(model => (
          <div key={model.id} className="card p-5 card-hover">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${model.enabled ? 'bg-emerald-50 text-emerald-600' : 'bg-slate-100 text-slate-400'}`}>
                  <Settings size={20} />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-800">{model.provider}</h3>
                  <p className="text-xs text-slate-500">{model.modelName}</p>
                </div>
              </div>
              <div className="flex items-center gap-1">
                {canEdit() && (
                  <>
                    <button onClick={() => openModal(model)} className="p-1 hover:bg-slate-100 rounded"><Edit2 size={14} className="text-slate-500" /></button>
                    <button onClick={() => remove(model.id)} className="p-1 hover:bg-red-50 rounded"><Trash2 size={14} className="text-red-500" /></button>
                  </>
                )}
              </div>
            </div>
            <div className="space-y-2 mb-4 text-sm">
              <div className="flex justify-between"><span className="text-slate-500">Base URL</span><span className="text-slate-700 truncate max-w-[180px]">{model.baseUrl}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">API Key</span><span className={`badge ${getStatusColor(model.apiKeyConfigured ? '已配置' : '未配置')}`}>{model.apiKeyConfigured ? '已配置' : '未配置'}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Temperature</span><span className="text-slate-700">{model.temperature}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">每日预算</span><span className="text-slate-700">${model.dailyBudget}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">每月预算</span><span className="text-slate-700">${model.monthlyBudget}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Max Tokens</span><span className="text-slate-700">{model.maxTokens}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">备用模型</span><span className="text-slate-700">{model.backupModel || '-'}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">调用 / 失败</span><span className="text-slate-700">{model.callLogs} / {model.failureCount}</span></div>
              {model.lastCalledAt && <div className="flex justify-between"><span className="text-slate-500">最近调用</span><span className="text-slate-700 text-xs">{formatDateShort(model.lastCalledAt)}</span></div>}
            </div>
            <div className="flex items-center justify-between pt-4 border-t border-slate-100">
              <div className="flex items-center gap-2">
                <Activity size={14} className={model.enabled ? 'text-emerald-500' : 'text-slate-400'} />
                <span className={`text-sm ${model.enabled ? 'text-emerald-600' : 'text-slate-500'}`}>{model.enabled ? '已启用' : '已禁用'}</span>
              </div>
              {canEdit() && (
                <button onClick={() => toggleEnable(model.id)} className={`relative w-11 h-6 rounded-full transition-colors ${model.enabled ? 'bg-emerald-500' : 'bg-slate-300'}`}>
                  <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${model.enabled ? 'translate-x-5' : ''}`} />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="card p-5">
        <h3 className="font-semibold text-slate-800 mb-3 flex items-center gap-2"><Server size={16} /> 预留后端接口 (Cloudflare Workers)</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {apiEndpoints.map(ep => (
            <div key={ep.path} className="p-3 bg-slate-50 rounded-lg border border-slate-200">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-[10px] px-1.5 py-0.5 bg-primary-100 text-primary-700 rounded font-medium">{ep.method}</span>
                <span className="text-sm font-mono text-slate-700">{ep.path}</span>
              </div>
              <p className="text-xs text-slate-500">{ep.desc}</p>
            </div>
          ))}
        </div>
        <p className="text-xs text-slate-400 mt-3">提示: 第一版为 mock 结构，后续在 Cloudflare Workers 中实现真实逻辑</p>
      </div>

      <div className="card p-5">
        <h3 className="font-semibold text-slate-800 mb-3 flex items-center gap-2"><Shield size={16} /> 安全说明</h3>
        <ul className="text-sm text-slate-600 space-y-2 list-disc pl-5">
          <li>前端不保存任何 API Key 明文，仅记录是否已配置的状态</li>
          <li>真实的 API Key 将通过 Cloudflare Workers 环境变量注入</li>
          <li>所有实际 API 调用都通过后端代理，避免 Key 泄露</li>
          <li>预留 Supabase 接口用于后续持久化配置</li>
        </ul>
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-lg">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between">
              <h3 className="text-lg font-bold">{editingModel ? '编辑配置' : '新增配置'}</h3>
              <button onClick={() => setShowModal(false)} className="p-1 hover:bg-slate-100 rounded"><X size={18} /></button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">模型供应商</label>
                <select className="select" value={form.provider} onChange={e => setForm({ ...form, provider: e.target.value })}>
                  <option value="OpenAI">OpenAI</option>
                  <option value="Gemini">Gemini</option>
                  <option value="中转 API">中转 API</option>
                  <option value="Custom OpenAI Compatible API">Custom OpenAI Compatible API</option>
                </select>
              </div>
              <div><label className="block text-sm font-medium text-slate-700 mb-1">Base URL</label><input className="input" value={form.baseUrl} onChange={e => setForm({ ...form, baseUrl: e.target.value })} placeholder="https://api..." /></div>
              <div><label className="block text-sm font-medium text-slate-700 mb-1">模型名称</label><input className="input" value={form.modelName} onChange={e => setForm({ ...form, modelName: e.target.value })} /></div>
              <div><label className="block text-sm font-medium text-slate-700 mb-1">API Key 状态</label>
                <select className="select" value={form.apiKeyConfigured} onChange={e => setForm({ ...form, apiKeyConfigured: e.target.value === 'true' })}>
                  <option value="false">未配置</option><option value="true">已配置</option>
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="block text-sm font-medium text-slate-700 mb-1">Temperature</label><input type="number" step="0.1" className="input" value={form.temperature} onChange={e => setForm({ ...form, temperature: Number(e.target.value) })} /></div>
                <div><label className="block text-sm font-medium text-slate-700 mb-1">Max Tokens</label><input type="number" className="input" value={form.maxTokens} onChange={e => setForm({ ...form, maxTokens: Number(e.target.value) })} /></div>
                <div><label className="block text-sm font-medium text-slate-700 mb-1">每日预算 ($)</label><input type="number" className="input" value={form.dailyBudget} onChange={e => setForm({ ...form, dailyBudget: Number(e.target.value) })} /></div>
                <div><label className="block text-sm font-medium text-slate-700 mb-1">每月预算 ($)</label><input type="number" className="input" value={form.monthlyBudget} onChange={e => setForm({ ...form, monthlyBudget: Number(e.target.value) })} /></div>
              </div>
              <div><label className="block text-sm font-medium text-slate-700 mb-1">备用模型</label><input className="input" value={form.backupModel} onChange={e => setForm({ ...form, backupModel: e.target.value })} /></div>
            </div>
            <div className="p-6 border-t border-slate-200 flex justify-end gap-3">
              <button onClick={() => setShowModal(false)} className="btn-secondary">取消</button>
              <button onClick={save} className="btn-primary">保存</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
